function getFirstDefinedValue(values) {
  const resolved_value = values.find((value) => value != null && value !== '') ?? null;
  return resolved_value;
}

export function updateQuestionUnassigningState(question, target_question_id, is_unassigning) {
  const question_id_value = getFirstDefinedValue([
    question?.question_id,
    question?.backend_id,
    question?.id,
    null,
  ]);
  const question_id = Number(question_id_value);
  const is_target_question = question_id === target_question_id;
  const next_question = is_target_question
    ? { ...question, is_unassigning: is_unassigning }
    : question;
  return next_question;
}

function getFallbackGroup(ctx) {
  const fallback_group = ctx.main_sections?.[0] || ctx.section_groups?.[0] || {};
  return fallback_group;
}

function getActiveMainSection(ctx, active_main_section_id) {
  const active_main_section = ctx.section_groups.find((group) => {
    const is_match = String(group.form_main_section_id) === String(active_main_section_id);
    return is_match;
  });
  return active_main_section;
}

function resolveMainSectionModalId(fallback_group, main_section_id) {
  const resolved_main_section_id = getFirstDefinedValue([
    main_section_id,
    fallback_group.form_main_section_id,
    fallback_group.backend_id,
    '',
  ]);
  const normalized_main_section_id = resolved_main_section_id === '' || resolved_main_section_id == null
    ? ''
    : String(resolved_main_section_id);
  return normalized_main_section_id;
}

function resolveMainSectionModalName(active_main_section, fallback_group, active_main_section_id) {
  const raw_name = getFirstDefinedValue([
    active_main_section?.main_section_name,
    fallback_group.main_section_name,
    fallback_group.name,
    '',
  ]);
  const active_main_section_name = active_main_section_id === ''
    ? ''
    : String(raw_name).trim();
  return active_main_section_name;
}

export function buildSubSectionModalState(ctx, main_section_id) {
  const fallback_group = getFallbackGroup(ctx);
  const active_main_section_id = resolveMainSectionModalId(fallback_group, main_section_id);
  const active_main_section = getActiveMainSection(ctx, active_main_section_id);
  const active_main_section_name = resolveMainSectionModalName(
    active_main_section,
    fallback_group,
    active_main_section_id
  );
  const modal_state = {
    active_main_section,
    active_main_section_id,
    active_main_section_name,
  };
  return modal_state;
}

export function resolveSectionRef(ctx, section_ref) {
  const section = section_ref && typeof section_ref === 'object'
    ? section_ref
    : ctx.sections[Number(section_ref)] || null;
  return section;
}

function resolveQuestionModalIndex(ctx, section_ref, section) {
  const is_numeric_ref = typeof section_ref === 'number';
  const resolved_section_index = ctx.sections.indexOf(section);
  const section_index = is_numeric_ref && section ? section_ref : resolved_section_index;
  return section_index;
}

function buildQuestionModalForms(ctx, section) {
  const next_order_number = ((section?.questions ?? []).length || 0) + 1;
  const question_forms = [ctx.build_question_form(next_order_number)];
  return question_forms;
}

export function resolveQuestionModalState(ctx, section_ref) {
  const section = resolveSectionRef(ctx, section_ref);
  const section_index = resolveQuestionModalIndex(ctx, section_ref, section);
  const question_forms = buildQuestionModalForms(ctx, section);
  const modal_state = {
    section,
    section_index,
    question_forms,
  };
  return modal_state;
}

export function buildEditableSectionState(section_id, section) {
  const editable_section_state = {
    form_section_id: section?.form_section_id || section_id,
    section_id: section?.section_id || section_id,
    name: section.name || '',
    form_instructions: Array.isArray(section?.form_instructions) && section.form_instructions.length
      ? section.form_instructions
      : [{ content: '', type: 'paragraph' }],
    is_deleting: Boolean(section?.is_deleting),
  };
  return editable_section_state;
}

export function buildEditableMainSectionState(main_section_id, main_section) {
  const editable_main_section_state = {
    main_section_id: main_section_id,
    main_section_name: String(main_section?.main_section_name ?? main_section?.name ?? '').trim(),
    main_section_description: String(main_section?.description ?? '').trim(),
    is_deleting: Boolean(main_section?.is_deleting),
  };
  return editable_main_section_state;
}

export function mapMainSectionsWithQuestions(main_sections, target_section_id, question_id, is_unassigning, resolveSectionId, mapQuestionsWithUnassigningState) {
  const next_main_sections = (Array.isArray(main_sections) ? main_sections : []).map((main_section) => {
    const next_main_section = {
      ...main_section,
      sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) => {
        if (resolveSectionId(section) !== target_section_id) {
          return section;
        }

        const next_section = {
          ...section,
          questions: mapQuestionsWithUnassigningState(section.questions, question_id, is_unassigning),
        };
        return next_section;
      }),
    };
    return next_main_section;
  });
  return next_main_sections;
}

export function mapSectionsDeletingState(main_sections, sections, target_section_id, is_deleting, updateSectionDeletingState) {
  const next_state = {
    main_sections: (Array.isArray(main_sections) ? main_sections : []).map((main_section) => {
      const next_main_section = {
        ...main_section,
        sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) => {
          const next_section = updateSectionDeletingState(section, target_section_id, is_deleting);
          return next_section;
        }),
      };
      return next_main_section;
    }),
    sections: (Array.isArray(sections) ? sections : []).map((section) => {
      const next_section = updateSectionDeletingState(section, target_section_id, is_deleting);
      return next_section;
    }),
  };
  return next_state;
}

export function findProjectQuestion(rows, normalized_question_id) {
  const matched_question = (Array.isArray(rows) ? rows : []).find((item) => {
    const is_match = Number(item?.question_id ?? item?.id ?? null) === normalized_question_id;
    return is_match;
  }) || null;
  return matched_question;
}

export function resolveProjectId(matched_question) {
  const project_id = getFirstDefinedValue([
    matched_question?.project_id,
    matched_question?.project?.id,
    matched_question?.question?.project_id,
    matched_question?.question?.project?.id,
    '',
  ]);
  return project_id;
}
